import React, { useRef } from "react";
import { useTransform, motion, useScroll } from "framer-motion";
import {
  ProductReviewsSVG,
  MonetizeSVG,
  ConnectBrandsSVG,
  DirectCommSVG,
} from "./feature-svgs";

const features = [
  {
    title: "Product Reviews",
    description:
      "Create and participate in campaigns focused on authentic product reviews. Share your honest opinions with your audience.",
    color: "#3B5BA5",
    SvgComponent: ProductReviewsSVG,
  },
  {
    title: "Monetize Your Influence",
    description:
      "Earn money or receive products for sharing your honest opinions. Turn your content into real compensation.",
    color: "#10b981",
    SvgComponent: MonetizeSVG,
  },
  {
    title: "Connect with Brands",
    description:
      "Build relationships with brands that align with your content and audience. Find the perfect match for your niche.",
    color: "#8b5cf6",
    SvgComponent: ConnectBrandsSVG,
  },
  {
    title: "Direct Communication",
    description:
      "Message brands directly to discuss campaign details, requirements, and compensation without intermediaries.",
    color: "#f59e0b",
    SvgComponent: DirectCommSVG,
  },
];

function Card({ i, title, description, color, SvgComponent, progress, range, targetScale }) {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start end", "start start"],
  });

  const imageScale = useTransform(scrollYProgress, [0, 1], [2, 1]);
  const scale = useTransform(progress, range, [1, targetScale]);

  return (
    <div
      ref={container}
      className="h-screen flex items-center justify-center sticky top-0"
    >
      <motion.div
        style={{
          backgroundColor: color,
          scale,
          top: `calc(-5vh + ${i * 25}px)`,
        }}
        className="flex flex-col relative -top-[10%] h-[450px] w-[90%] max-w-2xl rounded-2xl lg:p-10 sm:p-6 p-4 origin-top shadow-2xl"
      >
        <h2 className="text-2xl text-center font-semibold text-white">{title}</h2>
        <div className="flex h-full mt-5 gap-6 sm:gap-10">
          <div className="w-[40%] relative top-[10%]">
            <p className="text-sm text-white/90 leading-relaxed">{description}</p>
          </div>

          <div className="relative w-[60%] h-full rounded-xl overflow-hidden bg-white">
            <motion.div className="w-full h-full" style={{ scale: imageScale }}>
              <SvgComponent />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function ScrollCard() {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });

  return (
    <main ref={container} className="bg-white">
      {/* Intro section */}
        <section className="text-gray-900 h-[20vh] w-full bg-white grid place-content-center pb-8">
          <div className="absolute bottom-0 left-0 right-0 top-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-size-[54px_54px] mask-[radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
          <div className="text-center relative z-10 px-8">
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Features</h2>
            <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-[120%]">
              Everything you need to <span className="text-primary">succeed</span>
            </h1>
            <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">
              Scroll down to explore our platform's key features
            </p>
          </div>
        </section>

        {/* Stacking cards */}
        <section className="text-white w-full bg-white">
          {features.map((feature, i) => {
            const targetScale = 1 - (features.length - i) * 0.05;
            return (
              <Card
                key={`feature_${i}`}
                i={i}
                title={feature.title}
                description={feature.description}
                color={feature.color}
                SvgComponent={feature.SvgComponent}
                progress={scrollYProgress}
                range={[i * 0.25, 1]}
                targetScale={targetScale}
              />
            );
          })}
        </section>
    </main>
  );
}
